import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Megaphone, Plus, Trash, PaperPlaneRight, PaperPlaneTilt } from "@phosphor-icons/react";

export default function Announcements() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", message: "", audience: "all" });
  const [showForm, setShowForm] = useState(false);
  const [sending, setSending] = useState(null);

  async function reload() { const r = await api.get("/announcements"); setItems(r.data); }
  useEffect(() => { reload(); }, []);

  async function submit(e) {
    e.preventDefault();
    try {
      await api.post("/announcements", form);
      toast.success("Announcement posted");
      setForm({ title: "", message: "", audience: "all" }); setShowForm(false); reload();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this announcement?")) return;
    await api.delete(`/announcements/${id}`); reload();
  }

  async function emailAll(id) {
    if (!window.confirm("Email this announcement to all parents with email on file?")) return;
    setSending(id);
    try {
      const r = await api.post(`/emails/announcement/${id}`, {});
      toast.success(`Sent to ${r.data.sent} parent${r.data.sent === 1 ? "" : "s"}`);
      if (r.data.failed?.length) toast.error(`${r.data.failed.length} failed`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(null); }
  }

  const inputCls = "w-full h-12 px-3 bg-[#0a0a0a] border border-neutral-800 rounded-md focus:outline-none focus:border-[#D90429]";

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Broadcast</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">Announcements</h1>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          data-testid="new-announcement-btn"
          className="h-14 px-6 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white font-display font-bold uppercase tracking-wider inline-flex items-center gap-2"
        >
          <Plus size={22} weight="bold" /> New
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-[#121212] border border-neutral-800 rounded-lg p-6 space-y-3">
          <input required placeholder="Title" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ann-title" />
          <textarea required rows={4} placeholder="Message" className="w-full p-3 bg-[#0a0a0a] border border-neutral-800 rounded-md focus:outline-none focus:border-[#D90429]" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="ann-message" />
          <select className={inputCls} value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}>
            <option value="all">Everyone</option>
            <option value="coaches">Coaches only</option>
          </select>
          <button type="submit" data-testid="submit-ann-btn" className="h-12 px-6 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white font-display font-bold uppercase tracking-wider inline-flex items-center gap-2">
            <PaperPlaneRight size={18} weight="bold" /> Post
          </button>
        </form>
      )}

      {items.length === 0 ? (
        <div className="bg-[#121212] border border-neutral-800 rounded-lg p-10 text-center overline">No announcements yet</div>
      ) : (
        <div className="space-y-4">
          {items.map((a) => (
            <div key={a.id} className="bg-[#121212] border border-neutral-800 rounded-lg p-5">
              <div className="flex justify-between items-start gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-md bg-[#D90429] flex items-center justify-center shrink-0">
                    <Megaphone size={20} weight="fill" color="#fff" />
                  </div>
                  <div>
                    <div className="font-display font-black text-xl uppercase tracking-tight">{a.title}</div>
                    <div className="overline mt-1">{a.author_name} • {new Date(a.created_at).toLocaleString()} • {a.audience}</div>
                  </div>
                </div>
                <button onClick={() => remove(a.id)} className="text-neutral-500 hover:text-[#D90429]"><Trash size={18} weight="bold" /></button>
              </div>
              <div className="mt-4 text-sm text-neutral-300 whitespace-pre-wrap">{a.message}</div>
              <button
                onClick={() => emailAll(a.id)}
                disabled={sending === a.id}
                data-testid={`ann-email-${a.id}`}
                className="mt-4 h-11 px-4 rounded-md bg-[#06D6A0] hover:bg-[#05b587] text-[#050505] font-display font-bold uppercase tracking-wider text-xs inline-flex items-center gap-2 disabled:opacity-60"
              >
                <PaperPlaneTilt size={14} weight="bold" /> {sending === a.id ? "Sending…" : "Email to Parents"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
